import { DocumentNode, gql, useMutation } from '@apollo/client';
import { Text } from '@chakra-ui/react';
import * as React from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';

import { logError } from '@/imports/logging/ClientLogger';
import { Card } from '@/imports/ui/chakra/components/Card';

import { BannerImageCard_GroupFragment } from './graphql-types/BannerImageCard_GroupFragment';
import {
  BannerImageCard_UpdateGroup,
  BannerImageCard_UpdateGroupVariables,
} from './graphql-types/BannerImageCard_UpdateGroup';

import { GroupBannerImagePicker } from '../../../GroupBannerImagePicker';

const UPDATE_GROUP = gql`
  mutation BannerImageCard_UpdateGroup($input: UpdateGroupInput!) {
    updateGroup(input: $input) {
      data {
        _id
        bannerImage {
          _id
          imageProxy(input: { ar: "8:3", w: 1600, fit: "fill", fill: "blur" })
        }
      }
    }
  }
`;

type IBannerImageCardForm = {
  bannerImageId?: string;
};

export const BannerImageCard: React.FC<{
  group: BannerImageCard_GroupFragment;
}> & { groupFragment: DocumentNode } = ({ group }) => {
  const [updateGroup] = useMutation<
    BannerImageCard_UpdateGroup,
    BannerImageCard_UpdateGroupVariables
  >(UPDATE_GROUP);

  const methods = useForm<IBannerImageCardForm>({
    defaultValues: {
      bannerImageId: group.bannerImage?._id ?? undefined,
    },
  });

  const bannerImageId = methods.watch('bannerImageId');

  React.useEffect(() => {
    const updateBannerImage = async (): Promise<void> => {
      try {
        await updateGroup({
          variables: {
            input: {
              id: group._id,
              data: { groupBannerImageId: bannerImageId },
            },
          },
        });
      } catch (e) {
        logError(e);
      }
    };
    void updateBannerImage();
  }, [bannerImageId, group._id, updateGroup]);

  return (
    <FormProvider {...methods}>
      <Card p="6" w="full">
        <Text textStyle="xlBodyStrong" mb="4">
          Banner Image
        </Text>
        <Controller
          control={methods.control}
          name="bannerImageId"
          render={({ field }) => (
            <GroupBannerImagePicker
              {...field}
              nonDraftImageProxy={group.bannerImage?.imageProxy ?? null}
            />
          )}
        />
      </Card>
    </FormProvider>
  );
};

BannerImageCard.groupFragment = gql`
  fragment BannerImageCard_GroupFragment on Group {
    _id
    bannerImage {
      _id
      imageProxy(input: { ar: "8:3", w: 1600, fit: "fill", fill: "blur" })
    }
  }
`;
